import React, { useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import classNames from "classnames";
import WidgetCard from "../_shared/WidgetCard";
import PortalMenu from "../_shared/PortalMenu";
import { ChevronDown } from "../_shared/icons";
import { registerHeadOfficeCharts, hexToRgb } from "../_shared/registerCharts";
import { GrowthMetric, RangeDatum, THEME_PRIMARY } from "../data";
import { robotoFont } from "../theme/fonts";
import styles from "./style.module.scss";

registerHeadOfficeCharts();

interface ReadingGrowthTrendsProps {
  range: RangeDatum;
  metrics: GrowthMetric[];
  selected: boolean[];
  onToggle: (idx: number) => void;
  /** re-render trigger when the branding theme changes */
  themeKey: string;
}

/* distinct line colours (mirrors the Reading Mix / avatar palette) so each
   selected metric reads as its own line when several are compared */
/* left-axis title per API unit — the API sends `unit` alongside each metric */
const COUNT_AXIS_TITLES: Record<string, string> = {
  books: "No. of books",
  hundred_pages: "Pages (hundreds)",
  minutes: "Minutes",
};

/* Keep at least one colour per metric the API can return — the index wraps, so
   a short list makes two lines share a colour (Engagement wore Books Read's
   purple once the API grew to seven metrics). */
const LINE_COLORS = [
  "#9A469B",
  "#1897A6",
  "#F9A11B",
  "#EF437B",
  "#8CA00E",
  "#4F6C8F",
  "#C92A2A",
  "#12B886",
];

export default function ReadingGrowthTrends({
  range,
  metrics,
  selected,
  onToggle,
  themeKey,
}: ReadingGrowthTrendsProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selIdx = metrics.map((_, i) => i).filter((i) => selected[i]);
  const multi = selIdx.length > 1;

  const { chartData, options, legend } = useMemo(() => {
    const primary = THEME_PRIMARY[themeKey] ?? "#9A469B";
    const single = selIdx.length === 1;

    // a metric is a percentage (0–100) when its unit/key says so; those ride the
    // RIGHT axis. Everything else is a count (books/pages) on the LEFT axis.
    const isPct = (m: GrowthMetric) =>
      /%|rate|percent/i.test(`${m.unit} ${m.key}`);

    const anyCount = selIdx.some((i) => !isPct(metrics[i]));
    const anyPct = selIdx.some((i) => isPct(metrics[i]));

    // the left axis carries whatever non-percentage metrics are selected, and
    // those aren't always books (engagement is minutes, pages come in hundreds)
    const countUnits = Array.from(
      new Set(
        selIdx.filter((i) => !isPct(metrics[i])).map((i) => metrics[i].unit),
      ),
    );
    const countTitle =
      countUnits.length === 1
        ? (COUNT_AXIS_TITLES[countUnits[0]] ?? "No. of books")
        : "Value";

    const datasets = selIdx.map((i, n) => {
      const m = metrics[i];
      // raw values — no normalization; the two axes keep the scales honest
      const data = m.derive(range);
      const line = single ? primary : LINE_COLORS[n % LINE_COLORS.length];
      const rgb = hexToRgb(line);
      return {
        label: m.label,
        data,
        borderColor: line,
        backgroundColor: single ? `rgba(${rgb},0.12)` : "transparent",
        borderWidth: 2.6,
        tension: 0.4,
        fill: single,
        pointRadius: 3,
        pointBackgroundColor: line,
        // books/pages → left axis, rates → right (0–100%) axis
        yAxisID: isPct(m) ? "yPct" : "yCount",
      };
    });

    const axisTicks = {
      font: { family: robotoFont.style.fontFamily, size: 11 },
      color: "#868E96",
    };
    const opts: any = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: { legend: { display: false } },
      scales: {
        x: {
          grid: { display: false },
          ticks: axisTicks,
        },
        // LEFT: number of books / pages
        yCount: {
          type: "linear",
          position: "left",
          display: anyCount,
          beginAtZero: true,
          grid: { color: "rgba(134,142,150,0.14)" },
          ticks: axisTicks,
          title: {
            display: anyCount,
            text: countTitle,
            font: { family: robotoFont.style.fontFamily, size: 10.5 },
            color: "#868E96",
          },
        },
        // RIGHT: percentage 0–100
        yPct: {
          type: "linear",
          position: "right",
          display: anyPct,
          beginAtZero: true,
          max: 100,
          // don't paint a second grid over the left one
          grid: { drawOnChartArea: false },
          ticks: { ...axisTicks, callback: (v: any) => v + "%" },
          title: {
            display: anyPct,
            text: "Percentage",
            font: { family: robotoFont.style.fontFamily, size: 10.5 },
            color: "#868E96",
          },
        },
      },
    };

    const leg = selIdx.map((i, n) => ({
      label: metrics[i].label,
      color: single ? primary : LINE_COLORS[n % LINE_COLORS.length],
    }));

    return {
      chartData: { labels: range.months, datasets },
      options: opts,
      legend: leg,
    };
    // themeKey included so colours refresh on theme change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, selected, multi, themeKey]);

  const metricLabel =
    selIdx.length === 1 ? metrics[selIdx[0]].label : `${selIdx.length} metrics`;
  const sub =
    selIdx.length > 1
      ? "Counts on the left axis · rates (%) on the right"
      : metrics[selIdx[0]]?.sub;

  const dropdown = (
    <div className={styles.dropWrap} ref={ref}>
      <button
        type="button"
        className={classNames(styles.dropBtn, open && styles.dropBtnOpen)}
        onClick={() => setOpen((v) => !v)}
      >
        {metricLabel}
        <ChevronDown color="var(--ho-primary)" />
      </button>
      <PortalMenu
        anchorRef={ref}
        open={open}
        onClose={() => setOpen(false)}
        className={styles.menu}
      >
        <div className={styles.menuHint}>Select one or more to compare</div>
        {metrics.map((m, i) => {
          const on = selected[i];
          return (
            <button
              type="button"
              key={m.key}
              className={styles.menuItem}
              onClick={() => onToggle(i)}
            >
              <span className={classNames(styles.box, on && styles.boxOn)}>
                {on && (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </span>
              <span className={styles.menuLabel}>{m.label}</span>
            </button>
          );
        })}
      </PortalMenu>
    </div>
  );

  return (
    <WidgetCard
      title="Reading Growth Trends"
      subtitle={sub}
      right={dropdown}
      stretch
      flush
    >
      <div className={styles.legendRow}>
        {legend.map((gl, i) => (
          <div className={styles.legendItem} key={i}>
            <span
              className={styles.legendBar}
              style={{ background: gl.color, opacity: 1 }}
            />
            <span className={styles.legendLabel}>{gl.label}</span>
          </div>
        ))}
      </div>
      <div className={styles.chartBox}>
        <Line data={chartData} options={options} />
      </div>
    </WidgetCard>
  );
}
